import { APP_TIMEZONE, APP_LOCALE } from '@/config/timezone';

export const getStatusColor = (status: string | null | undefined) => {
  const normalized = status?.toLowerCase();

  if (!normalized) {
    return 'text-gray-600 bg-gray-100';
  }

  switch (normalized) {
    case 'performing':
      return 'text-green-600 bg-green-100';
    case '30–59 days delinquent':
    case '60–89 days delinquent':
    case '90+ days delinquent':
      return 'text-yellow-600 bg-yellow-100';
    case 'default':
      return 'text-red-600 bg-red-100';
    case 'foreclosure':
      return 'text-red-700 bg-red-200';
    case 'reo':
      return 'text-purple-600 bg-purple-100';
    case 'forbearance':
      return 'text-blue-600 bg-blue-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

/**
 * Format a value as currency with proper decimal places
 * @param value - The value to format (number, string, null, or undefined)
 * @param decimals - Number of decimal places (default: 2)
 * @param currency - Currency code: 'USD', 'EUR', 'GBP', 'CAD', 'AUD' (default: 'USD')
 * @returns Formatted currency string or '—' for invalid values
 */
export const formatCurrency = (
  value: number | string | null | undefined,
  decimals: number = 2,
  currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' = 'USD'
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '—';
  }

  // Use en-US locale for all currencies to ensure currency symbol is always in front
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    currencyDisplay: 'symbol', // Ensure symbol is used (not code)
  }).format(numValue);
};

/**
 * Format options for date display
 */
export type DateFormat = 'short' | 'long';

/**
 * Parse "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS" by splitting the string.
 * Returns a Date at local midnight of the correct calendar day.
 * Used when we already have an ISO string from the API and need a Date for Intl formatting.
 */
/**
 * Parse "YYYY-MM-DD" (or "YYYY-MM-DDTHH:MM:SS") into a Date at **UTC noon**.
 *
 * Why UTC noon? Intl.DateTimeFormat with timeZone:'America/New_York' converts
 * the Date's UTC instant to the target timezone. If we used midnight local
 * (new Date(y, m, d)), that instant could fall on the previous calendar day
 * in NY when the browser runs in UTC+N. UTC noon is guaranteed to be the
 * same calendar day in every timezone on earth (UTC-12 to UTC+14).
 *
 * Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/formatToParts
 */
export const parseISODate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

/**
 * Convert a Date object to "YYYY-MM-DD" string in APP_TIMEZONE.
 * Uses Intl.DateTimeFormat.formatToParts() so the result is explicitly
 * timezone-aware — even if the browser is in Tokyo, this returns the
 * date as it would be in America/New_York.
 */
export const toISODateInTZ = (date: Date): string => {
  const parts = new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year')!.value;
  const m = parts.find(p => p.type === 'month')!.value;
  const d = parts.find(p => p.type === 'day')!.value;
  return `${y}-${m}-${d}`;
};

/**
 * Format a date string in US format
 * @param dateString - The date string to format
 * @param format - Format style: 'short' for MM/DD/YYYY (default), 'long' for "Mar 20, 2023"
 * @returns Formatted date string or '—' for invalid dates
 */
export const formatDate = (
  dateString: string | null | undefined,
  format: DateFormat = 'short'
): string => {
  if (!dateString) {
    return '—';
  }

  const date = parseISODate(dateString);
  if (isNaN(date.getTime())) {
    return '—';
  }

  if (format === 'long') {
    return new Intl.DateTimeFormat(APP_LOCALE, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: APP_TIMEZONE,
    }).format(date);
  }

  return new Intl.DateTimeFormat(APP_LOCALE, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: APP_TIMEZONE,
  }).format(date);
};

/**
 * Format a date string in "mmm, dd, yyyy" format (e.g., "Jan 03, 2025")
 * @param dateString - The date string to format
 * @returns Formatted date string or '—' for invalid dates
 */
export const formatDateWithPaddedDay = (
  dateString: string | null | undefined
): string => {
  if (!dateString) {
    return '—';
  }

  const date = parseISODate(dateString);
  if (isNaN(date.getTime())) {
    return '—';
  }

  const parts = new Intl.DateTimeFormat(APP_LOCALE, {
    timeZone: APP_TIMEZONE,
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  const month = parts.find(p => p.type === 'month')!.value;
  const day = parts.find(p => p.type === 'day')!.value;
  const year = parts.find(p => p.type === 'year')!.value;

  return `${month} ${day}, ${year}`;
};

/**
 * Format a value as a percentage with configurable decimal places
 * @param value - The value to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string or '—' for invalid values
 */
export const formatPercentage = (
  value: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '—';
  }

  return `${formatNumber(numValue, decimals)}%`;
};

/**
 * Format a decimal value (0.5534) as a percentage (55.34%) with configurable decimal places
 * Converts decimal to percentage by multiplying by 100
 * @param value - The decimal value to format (e.g., 0.5534)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string or '—' for invalid values
 */
export const formatDecimalAsPercentage = (
  value: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '—';
  }

  // Convert decimal to percentage (0.5534 -> 55.34)
  const percentValue = numValue * 100;
  return `${formatNumber(percentValue, decimals)}%`;
};

/**
 * Format a number with US thousands separators
 * @param value - The value to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string or '—' for invalid values
 */
export const formatNumber = (
  value: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
};

/**
 * Check if a value is invalid or missing
 * @param value - The value to check
 * @returns True if the value is invalid or missing
 */
export const isInvalidValue = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && isNaN(value))
  );
};
